/**
 * PastTripModal - Modal for logging past trips
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon, History } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";

export const PastTripModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  saving,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <History className="w-6 h-6 text-secondary" />
            Log Past Trips
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Add trips you've taken before joining DuoLyst. They'll appear on your map and count towards your achievements!
          </p>
        </DialogHeader>

        <form 
          onSubmit={onSubmit} 
          className="px-6 pb-6 space-y-4 overflow-y-auto flex-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Title */}
          <div className="space-y-2">
            <Label className="text-charcoal dark:text-white font-medium">Trip Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Honeymoon in Paris, Family Trip to Japan"
              className="h-12 bg-background border-charcoal/10 dark:border-white/20 rounded-xl"
              data-testid="past-trip-title"
            />
          </div>

          {/* Country */}
          <div className="space-y-2">
            <Label className="text-charcoal dark:text-white font-medium">Country Visited *</Label>
            <LocationPicker
              value={{
                location: formData.location,
                country: formData.country,
                country_code: formData.country_code,
              }}
              onChange={(loc) => setFormData(prev => ({
                ...prev,
                location: loc.location,
                country: loc.country,
                country_code: loc.country_code,
              }))}
              placeholder="Select country for your map"
            />
          </div>

          {/* Visit Date */}
          <div className="space-y-2">
            <Label className="text-charcoal dark:text-white font-medium">When did you visit? *</Label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full h-12 justify-start text-left font-normal bg-background border-charcoal/10 dark:border-white/20 rounded-xl"
                  data-testid="past-trip-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                  {formData.visited_date ? (
                    format(formData.visited_date, "PPP")
                  ) : (
                    <span className="text-muted-foreground">Pick visit date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.visited_date}
                  onSelect={(date) => {
                    if (date) {
                      setFormData(prev => ({ ...prev, visited_date: date }));
                    }
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                  captionLayout="dropdown-buttons"
                  fromYear={1950}
                  toYear={new Date().getFullYear()}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-charcoal dark:text-white font-medium">Description (optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Share some memories from this trip..."
              className="min-h-[80px] bg-background border-charcoal/10 dark:border-white/20 rounded-xl resize-none"
              data-testid="past-trip-description"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label className="text-charcoal dark:text-white font-medium">Category</Label>
            <Select
              value={formData.category}
              onValueChange={(val) => setFormData({ ...formData, category: val })}
            >
              <SelectTrigger className="h-12 bg-background border-charcoal/10 dark:border-white/20 rounded-xl" data-testid="past-trip-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="travel">✈️ Travel</SelectItem>
                <SelectItem value="adventure">🏔️ Adventure</SelectItem>
                <SelectItem value="experiences">🎭 Experiences</SelectItem>
                <SelectItem value="romance">💕 Romance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Spacer for bottom padding */}
          <div className="h-4" />

          {/* Buttons - sticky at bottom */}
          <div className="flex gap-3 pt-4 pb-2 sticky bottom-0 bg-card">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              className="flex-1 h-12 rounded-xl border-charcoal/10 dark:border-white/20"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 h-12 bg-secondary hover:bg-secondary/90 text-white rounded-xl btn-press"
              data-testid="past-trip-submit"
            >
              {saving ? "Saving..." : "Add to Map"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PastTripModal;
